import React, { createContext, useContext, useMemo, ReactNode } from 'react';

/**
 * UI mode context type definition
 * Determines UI layout: full (Browser) vs minimal (WebView)
 */
export interface ViewModeContextType {
  /** Whether to show minimal UI for in-app browser (WebView mode) */
  isWebView: boolean;
}

/**
 * UI mode context for detecting UI layout mode
 * Controls whether to show full UI (AppBar, Footer) or minimal UI (transparent background)
 */
const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

/**
 * Read a cookie value by name
 */
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

/**
 * Provider component for UI mode context
 */
export const ViewModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Read IsWebView cookie (set by IdTokenHintValidationHandler when ?is_webview=true query parameter is present)
  // Cookie has HttpOnly=false so client can read it for UI layout decisions
  const viewMode = useMemo<ViewModeContextType>(() => ({
    isWebView: getCookie('IsWebView') === 'true',
  }), []);

  return <ViewModeContext.Provider value={viewMode}>{children}</ViewModeContext.Provider>;
};

/**
 * Hook to access UI mode context
 * Used to determine whether to show full or minimal UI layout
 * @throws Error if used outside ViewModeProvider
 */
export const useViewMode = (): ViewModeContextType => {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
};
